import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  PublicationStatus,
  Shift,
  WithPreparedAttributes,
  TimelineShift,
} from '@wilson/interfaces';
import {
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  BulkDeleteShiftActivitiesFromUnassignedRegion,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  ShiftTimelineDataTransformerService,
  ShiftTimelineSettingsState,
  ShiftTimelineUnassignedRegionState,
  TimelineAssignedShiftValidationsStateService,
  TimelineCreateNewShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';

@Injectable()
export class TimelineShiftAssignService {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  protected shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );

  async handleCellViewShiftsWithoutActivitiesAssignments(
    userId: string,
    selectedShifts: Array<TimelineShift>,
  ): Promise<void> {
    try {
      selectedShifts.forEach((selectedShift) => {
        if (selectedShift.userId) {
          this.reassignTimelineV2ShiftsInState(userId, selectedShift);
        } else {
          this.assignCellViewTimelineShifts(userId, selectedShift);
        }
      });
      await this.applyShiftAssignmentsAndRefresh(userId, selectedShifts);
    } catch {
      selectedShifts.forEach((selectedShift) => {
        if (selectedShift.userId) {
          this.errorHandleV2ShiftReassignment(userId, selectedShift);
        } else {
          this.errorHandleCellViewShiftAssignment(userId, selectedShift);
        }
      });
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      const userIds = selectedShifts.reduce(
        (acc, shift) => {
          if (shift.userId) {
            acc.push(shift.userId);
          }
          return acc;
        },
        [userId] as string[],
      );
      this.timelineCommonOperationsService.updateUsersAssignmentsState(userIds);
    }
  }

  async handleStandardViewShiftsWithoutActivitiesAssignments(
    userId: string,
    selectedShifts: TimelineShift[],
  ): Promise<void> {
    try {
      selectedShifts.forEach((selectedShift) => {
        if (selectedShift.userId) {
          this.reassignTimelineV2ShiftsInState(userId, selectedShift);
        } else {
          this.assignStandardViewTimelineV2Shifts(userId, selectedShift);
        }
      });
      await this.applyShiftAssignmentsAndRefresh(userId, selectedShifts);
    } catch {
      selectedShifts.forEach((selectedShift) => {
        if (selectedShift.userId) {
          this.errorHandleV2ShiftReassignment(userId, selectedShift);
        } else {
          this.errorHandleStandardViewAssignment(userId, selectedShift);
        }
      });
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      const userIds = selectedShifts.reduce(
        (acc, shift) => {
          if (shift.userId) {
            acc.add(shift.userId);
          }
          return acc;
        },
        new Set([userId]) as Set<string>,
      );
      this.timelineCommonOperationsService.updateUsersAssignmentsState(
        Array.from(userIds),
      );
    }
  }

  async applyShiftAssignmentsAndRefresh(
    userId: string,
    selectedShifts: (Shift & WithPreparedAttributes & { id: string })[],
  ): Promise<void> {
    await firstValueFrom(
      this.shiftsService.updateShiftsBulk(
        selectedShifts.map((shift) => {
          return {
            id: shift.id,
            publicationStatus:
              shift.publicationStatus === PublicationStatus.Published
                ? PublicationStatus.NotPublishedAgain
                : shift.publicationStatus,
            userId,
            organizationalUnitId: shift.organizationalUnitId,
          };
        }),
      ),
    );

    selectedShifts.map((selectedShift) => {
      if (selectedShift.userId) {
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          userId,
          selectedShift.userId as string,
        ]);

        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          userId,
          selectedShift.userId as string,
        ]);
      } else {
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          userId,
        ]);
        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          userId,
        ]);
      }
    });

    this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
    const shiftIds = selectedShifts.map((shift) => shift.id as string);
    this.timelineAssignedShiftValidationsStateService.queueValidationRequest(
      shiftIds,
      {
        forceRefresh: true,
      },
    );
    this.nzMessageService.success(
      this.translate.instant(
        selectedShifts.length > 1
          ? 'shift.page.move_shifts_success'
          : 'shift.page.move_shift_success',
      ),
    );
  }

  private assignCellViewTimelineShifts(
    userId: string,
    selectedShift: TimelineShift,
  ): void {
    this.store.dispatch([
      new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
        shiftId: selectedShift.id,
      }),
      new TimelineCreateNewShiftV2(
        {
          ...selectedShift,
          userId,
          publicationStatus: PublicationStatus.NotPublished,
          isHighlighted: true,
        },
        userId,
      ),
    ]);
  }

  private assignStandardViewTimelineV2Shifts(
    userId: string,
    selectedShift: TimelineShift,
  ): void {
    const areActivitiesVisible = this.store.selectSnapshot(
      ShiftTimelineSettingsState.areActivitiesVisible,
    );
    if (areActivitiesVisible) {
      const shiftActivities = this.store.selectSnapshot(
        ShiftTimelineUnassignedRegionState.unassignedShiftActivitiesArray(
          selectedShift.id as string,
        ),
      );
      if (shiftActivities.length > 0) {
        this.store.dispatch([
          new TimelineUpsertActivityV2(shiftActivities, selectedShift.id),
          new BulkDeleteShiftActivitiesFromUnassignedRegion(selectedShift.id),
        ]);
      }
    }

    this.store.dispatch([
      new RemoveShiftWithoutActivitiesFromUnassignedRegion({
        shiftId: selectedShift.id,
      }),
      new TimelineCreateNewShiftV2(
        {
          ...selectedShift,
          userId,
          publicationStatus: PublicationStatus.NotPublished,
          isHighlighted: true,
        },
        userId,
      ),
    ]);
  }

  private reassignTimelineV2ShiftsInState(
    newUserId: string,
    selectedShift: TimelineShift,
  ): void {
    this.store.dispatch(
      new TimelineRemoveShiftFromUserV2({
        userId: selectedShift.userId as string,
        shiftId: selectedShift.id as string,
      }),
    );
    this.store.dispatch(
      new TimelineCreateNewShiftV2(
        {
          ...selectedShift,
          isHighlighted: true,
          userId: newUserId,
          publicationStatus: PublicationStatus.NotPublished,
        },
        newUserId,
      ),
    );
  }

  private errorHandleStandardViewAssignment(
    userId: string,
    selectedShift: TimelineShift,
  ): void {
    const transformedSelectedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        selectedShift,
      );
    this.store.dispatch([
      new AddShiftWithoutActivitiesToUnassignedRegion(transformedSelectedShift),
      new TimelineRemoveShiftFromUserV2({
        userId,
        shiftId: selectedShift.id,
      }),
    ]);
  }

  private errorHandleCellViewShiftAssignment(
    userId: string,
    selectedShift: TimelineShift,
  ): void {
    const transformedSelectedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        selectedShift,
      );
    this.store.dispatch([
      new AddShiftWithoutActivitiesToCellViewUnassignedRegion(
        transformedSelectedShift,
      ),
      new TimelineRemoveShiftFromUserV2({
        userId,
        shiftId: selectedShift.id,
      }),
    ]);
  }

  private errorHandleV2ShiftReassignment(
    userId: string,
    selectedShift: TimelineShift,
  ): void {
    this.store.dispatch([
      new TimelineRemoveShiftFromUserV2({
        userId,
        shiftId: selectedShift.id,
      }),

      new TimelineCreateNewShiftV2(
        selectedShift,
        selectedShift.userId as string,
      ),
    ]);
  }
}
